import { openKv, type Kv } from './kv';

/** Что делать с письмом в очереди после попытки отправки. */
export type SendOutcome = 'sent' | 'retry' | 'drop';

export interface OutboxItem {
  readonly id: string;
  readonly workspaceId: string;
  /** Чьё сообщение: после входа другого человека очередь прежнего не отправляется. */
  readonly userId?: string;
  /** Тело отправки, как его собрал редактор (то, что нужно серверу). */
  readonly payload: unknown;
  readonly queuedAt: number;
}

/** Очередь изменилась: индикатор обновляет число. */
export const OUTBOX_EVENT = 'chat-outbox';
const changed = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(OUTBOX_EVENT));
};

let kv: Kv<OutboxItem> | null = null;
const store = (): Kv<OutboxItem> => (kv ??= openKv<OutboxItem>('chat-outbox'));

/** Для тестов: подменить хранилище. */
export function useOutboxStore(next: Kv<OutboxItem> | null): void {
  kv = next;
}

export async function enqueue(item: OutboxItem): Promise<void> {
  await store().set(item.id, item);
  changed();
}

export async function dequeue(id: string): Promise<void> {
  await store().del(id);
  changed();
}

/** Очередь пространства в том порядке, в каком сообщения писали. */
export async function pending(workspaceId: string | null = null): Promise<OutboxItem[]> {
  const all = await store().all();
  return all.filter((i) => workspaceId === null || i.workspaceId === workspaceId).sort((a, b) => a.queuedAt - b.queuedAt);
}

export async function clearOutbox(): Promise<void> {
  await store().clear();
  changed();
}

/**
 * Отправляет очередь по порядку. На первом «повторить» останавливается: порядок сообщений не должен
 * перепутаться. Возвращает, сколько ушло.
 */
export async function flushOutbox(workspaceId: string | null, send: (item: OutboxItem) => Promise<SendOutcome>): Promise<number> {
  let sent = 0;
  for (const item of await pending(workspaceId)) {
    const outcome = await send(item);
    if (outcome === 'retry') break;
    await dequeue(item.id);
    if (outcome === 'sent') sent += 1;
  }
  return sent;
}

/** Убрать сообщения других людей (вошёл другой человек на том же устройстве). */
export async function dropForeign(userId: string): Promise<void> {
  for (const item of await store().all()) {
    if (item.userId !== undefined && item.userId !== userId) await dequeue(item.id);
  }
}
